import React, { useState, useCallback, useRef, useEffect } from "react";
import Modal from "@amzn/awsui-components-console/modal";
import Box from "@amzn/awsui-components-console/box";
import SpaceBetween from "@amzn/awsui-components-console/space-between";
import Button from "@amzn/awsui-components-console/button";
import Input from "@amzn/awsui-components-console/input";
import FormField from "@amzn/awsui-components-console/form-field";
import Alert from "@amzn/awsui-components-console/alert";
import { SCHEMA_NAME_PATTERN } from "./constants";

export interface SaveSchemaModalProps {
  visible: boolean;
  onDismiss: () => void;
  /**
   * Invoked when the user clicks Save Draft or Submit for Approval. The
   * parent owns all schema-persistence business logic — duplicate-name
   * checks, empty-schema guards, adapter calls. The modal renders the
   * returned result as an alert and starts a dismiss timer on success.
   */
  onSave: (input: {
    name: string;
    draft: boolean;
  }) => Promise<{ success: boolean; message: string }>;
}

interface AlertState {
  type: "success" | "error";
  content: string;
}

const SaveSchemaModal: React.FC<SaveSchemaModalProps> = ({
  visible,
  onDismiss,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (!value) {
      setNameError("Name is required");
    } else if (!SCHEMA_NAME_PATTERN.test(value)) {
      setNameError(
        "Name must be alphanumeric, hyphens, or underscores (max 128 characters)"
      );
    } else {
      setNameError(undefined);
    }
  }, []);

  const handleSubmit = useCallback(
    async (draft: boolean) => {
      if (!name || !SCHEMA_NAME_PATTERN.test(name)) {
        setNameError(
          "Name must be alphanumeric, hyphens, or underscores (max 128 characters)"
        );
        return;
      }

      setLoading(true);
      setAlert(null);

      try {
        const result = await onSave({ name, draft });
        if (result.success) {
          setAlert({
            type: "success",
            content: result.message || "Schema saved successfully.",
          });
          dismissTimerRef.current = setTimeout(() => {
            setName("");
            setNameError(undefined);
            setAlert(null);
            onDismiss();
          }, 1500);
        } else {
          setAlert({
            type: "error",
            content: result.message || "Failed to save schema.",
          });
        }
      } catch {
        setAlert({
          type: "error",
          content: "An unexpected error occurred while saving.",
        });
      } finally {
        setLoading(false);
      }
    },
    [name, onSave, onDismiss]
  );

  const handleDismiss = useCallback(() => {
    if (!loading) {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      setName("");
      setNameError(undefined);
      setAlert(null);
      onDismiss();
    }
  }, [loading, onDismiss]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Save Schema"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={loading || !name || !!nameError}
              loading={loading}
            >
              Save Draft
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSubmit(false)}
              disabled={loading || !name || !!nameError}
              loading={loading}
            >
              Submit for Approval
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {alert && (
          <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
            {alert.content}
          </Alert>
        )}
        <FormField
          label="Schema name"
          errorText={nameError}
          description="Alphanumeric characters, hyphens, and underscores only (max 128 characters)"
        >
          <Input
            value={name}
            onChange={({ detail }) => handleNameChange(detail.value)}
            placeholder="my-schema-name"
            disabled={loading}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default SaveSchemaModal;
